/**
 * The governed AUDIT PROJECTIONS, client half (#2444 phase 4, slice 6).
 *
 * Every read of the receipt and audit facts goes through a SECURITY DEFINER
 * projection with its own authorization check; ordinary roles hold no grant on
 * `core.request_receipt`, `core.audit_log` or `core.tool_invocation` (SPINE-P4
 * "Hard security contract"). This module is the ONE place a host names those
 * projections, their arguments and their columns, so a signature change on the
 * server is a one-file edit here — both hosts (console and portal) import it.
 *
 * ── what this module refuses to carry, by construction ─────────────────────
 * Each mapper below reads a NAMED ALLOWLIST of columns off the server row and
 * builds a fresh object. It never spreads the row. So a projection that one day
 * returned an extra column — a payload, a query, a header, a prompt, a tool's
 * arguments, a raw client id — cannot reach a screen or an export through this
 * module: nothing downstream ever holds it. `audit.test.ts` pins that with rows
 * that carry every forbidden field, and the mutation that spreads the row
 * reddens it by name.
 *
 * Labels are defended the same way. A label is a governed resolver's words
 * (the person/app-user title resolvers, the client and connector label
 * registries). A uuid-shaped string is never a label — if a projection ever
 * handed one back in a label column, `governedLabel` turns it into `null`, and
 * the hosts render their honest "not resolved" word instead of the key
 * (#1676 ruling 5: a machine key is never a name).
 *
 * The correlation id IS a uuid, and it is kept: it is the join key that opens
 * the correlation trace, and the CSV carries it for support. It is never a
 * label and no host renders it as text.
 *
 * ── the bounded range is enforced HERE too, not only in the filter bar ─────
 * The server refuses a range over 31 days. The filter bar refuses it first, so
 * a person is told in words. And `auditFilterArgs` THROWS on a missing or
 * over-long range, so no code path in any host can send an unbounded query
 * even if a UI forgot to check (fail closed, skill rule 6's shape one tier up).
 */
import type { Row, Transport } from './transport.js'

/** The two transport verbs the projections need; a host passes `client.raw()`. */
export type AuditDoor = Pick<Transport, 'rpcScalar' | 'rpcTable'>

/** How far a source claim was established (the v3 audit contract). */
export const SOURCE_TRUSTS = ['verified', 'declared', 'inferred'] as const
export type SourceTrust = typeof SOURCE_TRUSTS[number]

/** The server's cap on one query's time range, in days. */
export const AUDIT_RANGE_MAX_DAYS = 31
/** The server's cap on one page. */
export const AUDIT_PAGE_MAX = 100
/** What a host asks for when it does not say. */
export const AUDIT_PAGE_DEFAULT = 50

const UUID_ANYWHERE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
const DAY_MS = 86_400_000

/**
 * A governed label, or `null`.
 *
 * Non-empty text only. A string carrying a uuid anywhere in it is refused: a
 * label that is (or embeds) a machine key is exactly the defect #1676 ruling 5
 * names, and the hosts cannot tell a staff uuid from a record uuid, so they
 * show neither.
 */
export function governedLabel(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const text = value.trim()
  if (text === '' || UUID_ANYWHERE.test(text)) return null
  return text
}

/** What an actor label that could not be shown reads as — never the key, never "System". */
export const UNRESOLVED_ACTOR = 'Unresolved identity'

/**
 * An ACTOR label. Null from the projection means a system/installer act and
 * stays null (hosts say "System"). A value that is present but is not a
 * label — a uuid — is NOT a system act: it reads as `UNRESOLVED_ACTOR`, so a
 * projection regression never misattributes a person's act to the system.
 */
export function actorLabel(value: unknown): string | null {
  if (value == null) return null
  return governedLabel(value) ?? UNRESOLVED_ACTOR
}

/** A trust value the contract knows, or `null` for anything else. */
export function sourceTrust(value: unknown): SourceTrust | null {
  return (SOURCE_TRUSTS as readonly string[]).includes(value as string) ? value as SourceTrust : null
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null
const bool = (value: unknown): boolean => value === true
const int = (value: unknown): number | null => {
  const n = typeof value === 'string' ? Number(value) : value
  return typeof n === 'number' && Number.isFinite(n) ? Math.trunc(n) : null
}
/** A correlation key: kept only when it is uuid-shaped; never rendered. */
const correlation = (value: unknown): string | null =>
  typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ? value : null

/* ── 0. the words both hosts use ──────────────────────────────────────────── */

/**
 * The source-kind labels, as `core.request_source_kind` seeds them (core
 * 2026092307 §1). That table is closed to every API role, so a host cannot
 * read it; this mirror is the fallback when a projection returns only the key.
 * An unknown key reads "Unknown source" — never the key itself.
 */
export const SOURCE_KIND_LABELS: Readonly<Record<string, string>> = {
  console_web: 'Console',
  portal_web: 'Portal',
  mcp: 'MCP',
  api_integration: 'API integration',
  chrome_extension: 'Chrome extension',
  copilot: 'Copilot',
  automation: 'Automation',
  migration: 'Migration',
  unknown: 'Unknown source',
}

export function sourceKindLabel(kind: string | null | undefined): string {
  return (kind && SOURCE_KIND_LABELS[kind]) || 'Unknown source'
}

/**
 * How a host draws a trust level. The TONE is the kit's `Pill` tone vocabulary
 * (`good` / `neutral` / `warn`), carried as data so both hosts badge the same
 * level the same way; a level the contract does not know draws `unknown`.
 */
export const TRUST_PRESENTATION = {
  verified: { label: 'Verified', tone: 'good', description: 'The server established this source from a verified credential.' },
  declared: { label: 'Declared', tone: 'neutral', description: 'The client said where it came from; the server did not verify it.' },
  inferred: { label: 'Inferred', tone: 'warn', description: 'Neither verified nor declared; the server inferred the source.' },
} as const satisfies Record<SourceTrust, { label: string; tone: 'good' | 'neutral' | 'warn'; description: string }>

export const TRUST_UNKNOWN = {
  label: 'Trust unknown', tone: 'neutral', description: 'The record does not say how this source was established.',
} as const

export function trustPresentation(trust: SourceTrust | null) {
  return trust ? TRUST_PRESENTATION[trust] : TRUST_UNKNOWN
}

/* ── 1. record_last_change ─────────────────────────────────────────────────── */

/** `create | update | delete | act` per p4-api §1; `read` appears on the timeline. */
export type AuditOperation = 'create' | 'update' | 'delete' | 'read' | 'act'
const OPERATIONS: readonly AuditOperation[] = ['create', 'update', 'delete', 'read', 'act']
const operationOf = (value: unknown): AuditOperation | null =>
  OPERATIONS.find((op) => op === value) ?? null

export interface RecordLastChange {
  changedAt: string
  operation: AuditOperation | null
  /** The audit action key, e.g. `record.updated`. A key: hosts turn it into words. */
  action: string | null
  viaSourceKind: string | null
  /** The vocabulary's label for the source kind ("Console", "MCP", …). */
  viaSourceLabel: string
  viaTrust: SourceTrust | null
  viaClientLabel: string | null
  /** Null means a system/installer act — or, with `identityWithheld`, withheld. */
  actorLabel: string | null
  onBehalfOfLabel: string | null
  /**
   * TRUE only for a portal (external) caller when the actor is not one of their
   * own people: the host says "Changed by <brand>" and never a staff name.
   */
  identityWithheld: boolean
}

/** Maps one server row. Exported for the fixture and the tests. */
export function toRecordLastChange(row: Row | null | undefined): RecordLastChange | null {
  if (!row || typeof row !== 'object') return null
  const changedAt = text(row.changed_at)
  if (!changedAt) return null
  const withheld = bool(row.identity_withheld)
  return {
    changedAt,
    operation: operationOf(row.operation),
    action: text(row.action),
    viaSourceKind: text(row.via_source_kind),
    viaSourceLabel: governedLabel(row.via_source_label) ?? sourceKindLabel(text(row.via_source_kind)),
    viaTrust: sourceTrust(row.via_trust),
    /* A withheld identity is withheld WHOLE on this side too: even if a label
       arrived beside the flag, it is not carried. */
    viaClientLabel: withheld ? null : governedLabel(row.via_client_label),
    actorLabel: withheld ? null : actorLabel(row.actor_label),
    onBehalfOfLabel: withheld ? null : actorLabel(row.on_behalf_of_label),
    identityWithheld: withheld,
  }
}

/**
 * The latest AUTHORIZED change to one record, or `null` when there is none the
 * caller may see (the projection answers no row for a record the caller cannot
 * `can_see`, so "nothing" and "not yours" read the same — by design).
 *
 * One projection serves both hosts: the SERVER applies the portal identity rule
 * to an external caller (p4-api §1), so the portal calls the same function and
 * reads `identityWithheld`.
 */
export async function recordLastChange(door: AuditDoor, entity: string, id: string): Promise<RecordLastChange | null> {
  const [fn, schema] = AUDIT_PROJECTIONS.recordLastChange
  const rows = await door.rpcTable<Row>(fn, { p_entity: entity, p_id: id }, schema)
  return toRecordLastChange(rows[0])
}

/** Whether the caller may read the audit pages (platform admin or `audit_reader`). */
export async function isAuditReader(door: AuditDoor): Promise<boolean> {
  const [fn, schema] = AUDIT_PROJECTIONS.isAuditReader
  return (await door.rpcScalar<boolean | null>(fn, {}, schema)) === true
}

/* ── 1b. record_history — the record timeline's Changes lane ─────────────── */

/**
 * The actions whose `detail` the SERVER derives (core 2026092913's whitelist,
 * `core.audit_derived_detail`). A detail on any other action is not carried —
 * whatever arrives — so a projection regression that started echoing a
 * payload's free text could not reach a screen through this module.
 */
export const DERIVED_DETAIL_ACTIONS: readonly string[] = ['record.updated']

/** The CLOSED vocabulary of action words: the change operations, the Console's
 *  share acts, and the three legacy free-text keys older boxes still hold. */
const ACTION_WORDS: Readonly<Record<string, string>> = {
  'record.created': 'Created',
  'record.updated': 'Updated',
  'record.deleted': 'Deleted',
  'access.share.granted': 'Share granted',
  'access.share.revoked': 'Share revoked',
  // written by the Console before #2444 (free text the chain still holds, append-only)
  'share granted': 'Share granted',
  'share revoked': 'Share revoked',
  'owner reassigned': 'Owner reassigned',
}

/** An action KEY as core.audit accepts one since #2444 fix1: a dotted lowercase identifier. */
export const AUDIT_ACTION_KEY = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/

/**
 * Words for an action key, for a row the server gave no label: the closed
 * vocabulary above by name; any other DOTTED lowercase key (a module's
 * `compliance.risk.accepted`) in sentence case; anything else — free text,
 * capitals, spaces, a uuid — the neutral "Recorded change". A caller-chosen
 * sentence written as an action before core.audit refused one (#2444 fix1,
 * review-1 finding 2) therefore never becomes a card title.
 */
export function auditActionWords(action: string | null): string {
  if (!action) return 'Recorded change'
  const known = ACTION_WORDS[action]
  if (known) return known
  if (!AUDIT_ACTION_KEY.test(action)) return 'Recorded change'
  const words = action.replace(/[._]+/g, ' ').trim()
  if (!words || UUID_ANYWHERE.test(words)) return 'Recorded change'
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export interface RecordHistoryEntry {
  /** The chain position: orders the rows and keys them. Not an identity; never displayed. */
  seq: number | null
  at: string
  operation: AuditOperation | null
  /** The audit action key. A key: `actionLabel` is its words. */
  action: string | null
  /** The server's whitelisted label ("Owner reassigned"), else words for the key. */
  actionLabel: string
  viaSourceKind: string | null
  viaSourceLabel: string | null
  viaTrust: SourceTrust | null
  viaClientLabel: string | null
  /** Null means a system/installer act — or, with `identityWithheld`, withheld. */
  actorLabel: string | null
  onBehalfOfLabel: string | null
  identityWithheld: boolean
  /** Server-derived for a whitelisted action only ("Olga Owner → Nina New"); never a payload. */
  detail: string | null
}

/** Maps one server row. Exported for the fixture and the tests. */
export function toRecordHistoryEntry(row: Row | null | undefined): RecordHistoryEntry | null {
  if (!row || typeof row !== 'object') return null
  const at = text(row.at)
  if (!at) return null
  const action = text(row.action)
  const withheld = bool(row.identity_withheld)
  const derived = action != null && DERIVED_DETAIL_ACTIONS.includes(action)
  const sourceKind = text(row.via_source_kind)
  return {
    seq: int(row.seq),
    at,
    operation: operationOf(row.operation),
    action,
    actionLabel: (derived ? governedLabel(row.action_label) : null) ?? auditActionWords(action),
    viaSourceKind: sourceKind,
    /* An older row that recorded no channel has no "via" — not "Unknown source". */
    viaSourceLabel: sourceKind ? governedLabel(row.via_source_label) ?? sourceKindLabel(sourceKind) : null,
    viaTrust: sourceTrust(row.via_trust),
    viaClientLabel: withheld ? null : governedLabel(row.via_client_label),
    actorLabel: withheld ? null : actorLabel(row.actor_label),
    onBehalfOfLabel: withheld ? null : actorLabel(row.on_behalf_of_label),
    identityWithheld: withheld,
    detail: derived ? governedLabel(row.detail) : null,
  }
}

/**
 * The change history of ONE record the caller may see, newest first. Nothing
 * for a record the caller cannot `can_see` — the same nothing as a record with
 * no history. An audit reader gets every act; anyone else their own (the
 * server decides; the page asks the same question either way).
 */
export async function recordHistory(
  door: AuditDoor, entity: string, id: string, limit?: number,
): Promise<RecordHistoryEntry[]> {
  const [fn, schema] = AUDIT_PROJECTIONS.recordHistory
  const rows = await door.rpcTable<Row>(fn, { p_entity: entity, p_id: id, p_limit: pageLimit(limit) }, schema)
  return rows.map(toRecordHistoryEntry).filter((row): row is RecordHistoryEntry => row !== null)
}

/* ── 1c. my_tool_invocations — the caller's own Copilot history ─────────── */

export const TOOL_OUTCOMES = ['ok', 'refused', 'error'] as const
export type ToolOutcome = typeof TOOL_OUTCOMES[number]

export interface MyToolInvocation {
  at: string
  toolLabel: string | null
  module: string | null
  surface: string | null
  outcome: string
  durationMs: number | null
}

export interface MyToolInvocationPage { rows: MyToolInvocation[]; total: number }

/** NEVER the stored reason, arguments, prompt or response: rebuilt from the contract's names only. */
export function toMyToolInvocation(row: Row): MyToolInvocation {
  return {
    at: String(row.at ?? ''),
    toolLabel: governedLabel(row.tool_label),
    module: text(row.module),
    surface: text(row.surface),
    outcome: text(row.outcome) ?? 'unknown',
    durationMs: int(row.duration_ms),
  }
}

/** The caller's own tool calls (the server derives who that is), and how many there are under the same filter. */
export async function myToolInvocations(
  door: AuditDoor, opts: { outcomes?: readonly ToolOutcome[]; limit?: number } = {},
): Promise<MyToolInvocationPage> {
  const [fn, schema] = AUDIT_PROJECTIONS.myToolInvocations
  const outcomes = opts.outcomes?.filter((o) => (TOOL_OUTCOMES as readonly string[]).includes(o))
  const rows = await door.rpcTable<Row>(fn, {
    p_outcomes: outcomes && outcomes.length > 0 ? outcomes : null,
    p_limit: pageLimit(opts.limit ?? 40),
  }, schema)
  return { rows: rows.map(toMyToolInvocation), total: int(rows[0]?.total) ?? 0 }
}

/* ── 2. the bounded filter ─────────────────────────────────────────────────── */

export interface AuditRange { from: string; to: string }

export interface AuditTimelineFilter extends AuditRange {
  /** A picker HANDLE (a `core_app_user` / automation principal record id). Never displayed. */
  actor?: string
  /** A picker handle (a `core_organization` record id). Never displayed. */
  account?: string
  entity?: string
  operation?: AuditOperation
  action?: string
  source?: string
  trust?: SourceTrust
  /** A client LABEL exactly as displayed. */
  client?: string
}

export interface ToolInvocationFilter extends AuditRange {
  tool?: string
  outcome?: string
  surface?: string
  source?: string
  trust?: SourceTrust
  actor?: string
}

/**
 * Why a range cannot be asked, in words a person can act on — or `null` when
 * it is a valid, bounded range. The filter bar shows this; `auditFilterArgs`
 * throws it. Mirrors the server: both ends required, `to > from`,
 * `to - from <= 31 days`.
 */
export function auditRangeProblem(range: Partial<AuditRange>): string | null {
  if (!range.from || !range.to) return 'Choose both a start and an end date.'
  const from = Date.parse(range.from)
  const to = Date.parse(range.to)
  if (Number.isNaN(from) || Number.isNaN(to)) return 'Enter the dates as calendar dates.'
  if (from >= to) return 'The start date is after the end date.'
  if (to - from > AUDIT_RANGE_MAX_DAYS * DAY_MS) {
    return `Choose a range of ${AUDIT_RANGE_MAX_DAYS} days or fewer.`
  }
  return null
}

/** The default range: the last seven days, ending now. */
export function defaultAuditRange(now: Date = new Date()): AuditRange {
  return { from: new Date(now.getTime() - 7 * DAY_MS).toISOString(), to: now.toISOString() }
}

const FILTER_KEYS = ['from', 'to', 'actor', 'account', 'entity', 'operation', 'action', 'source', 'trust', 'client', 'tool', 'outcome', 'surface'] as const

/**
 * The `p_filter` document. THROWS on an unbounded or over-long range — the
 * last line of defence, so no host can send one even by mistake. Only the
 * contract's keys are sent (the server refuses an unknown key with 22023), and
 * empty optional filters are dropped rather than sent as `''`.
 */
export function auditFilterArgs(filter: AuditTimelineFilter | ToolInvocationFilter): Row {
  const problem = auditRangeProblem(filter)
  if (problem) throw new RangeError(`audit filter refused: ${problem}`)
  const out: Row = {}
  const source = filter as unknown as Readonly<Record<string, unknown>>
  for (const key of FILTER_KEYS) {
    const value = source[key]
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim()
  }
  return out
}

const pageLimit = (limit: number | undefined): number =>
  Math.min(AUDIT_PAGE_MAX, Math.max(1, Math.trunc(limit ?? AUDIT_PAGE_DEFAULT)))

/* ── 3. audit_timeline ─────────────────────────────────────────────────────── */

export interface AuditTimelineRow {
  at: string
  action: string
  operation: AuditOperation | null
  entityLabel: string | null
  recordLabel: string | null
  actorLabel: string | null
  onBehalfOfLabel: string | null
  accountLabel: string | null
  sourceKind: string | null
  sourceLabel: string
  sourceTrust: SourceTrust | null
  clientLabel: string | null
  /** A join key for the correlation trace. Never displayed. */
  correlationId: string | null
  hasRequestReceipt: boolean
  hasToolInvocation: boolean
}

export interface AuditPage<R> { rows: R[]; nextCursor: string | null }

export function toAuditTimelineRow(row: Row): AuditTimelineRow {
  return {
    at: String(row.at ?? ''),
    action: text(row.action) ?? 'unknown',
    operation: operationOf(row.operation),
    entityLabel: governedLabel(row.entity_label),
    recordLabel: governedLabel(row.record_label),
    actorLabel: actorLabel(row.actor_label),
    onBehalfOfLabel: actorLabel(row.on_behalf_of_label),
    accountLabel: governedLabel(row.account_label),
    sourceKind: text(row.source_kind),
    sourceLabel: governedLabel(row.source_label) ?? sourceKindLabel(text(row.source_kind)),
    sourceTrust: sourceTrust(row.source_trust),
    clientLabel: governedLabel(row.client_label),
    correlationId: correlation(row.correlation_id),
    hasRequestReceipt: bool(row.has_request_receipt),
    hasToolInvocation: bool(row.has_tool_invocation),
  }
}

/**
 * The page's rows and the keyset cursor for the next one. The projection
 * returns the cursor on every row (the same value); a null cursor, or an
 * empty page, is the last page. The cursor is opaque and passed back verbatim.
 */
function pageOf<R>(rows: Row[], map: (row: Row) => R): AuditPage<R> {
  const nextCursor = rows.length ? text(rows[rows.length - 1]!.next_cursor) : null
  return { rows: rows.map(map), nextCursor }
}

export async function auditTimeline(
  door: AuditDoor, filter: AuditTimelineFilter, cursor: string | null = null, limit?: number,
): Promise<AuditPage<AuditTimelineRow>> {
  const [fn, schema] = AUDIT_PROJECTIONS.timeline
  const rows = await door.rpcTable<Row>(fn, {
    p_filter: auditFilterArgs(filter), p_cursor: cursor, p_limit: pageLimit(limit),
  }, schema)
  return pageOf(rows, toAuditTimelineRow)
}

/* ── 4. correlation_trace ──────────────────────────────────────────────────── */

export type CorrelationFactKind = 'receipt' | 'audit' | 'tool'

export interface CorrelationFact {
  kind: CorrelationFactKind
  at: string
  /** audit: the action key; tool: the tool name. A key — hosts say it in words. */
  action: string | null
  operation: string | null
  entityLabel: string | null
  recordLabel: string | null
  actorLabel: string | null
  onBehalfOfLabel: string | null
  sourceKind: string | null
  sourceLabel: string
  sourceTrust: SourceTrust | null
  clientLabel: string | null
  /** receipt + tool: ok / refused / error. */
  outcome: string | null
  /** receipt: the HTTP status class, 1..5. */
  statusClass: number | null
  durationMs: number | null
  surface: string | null
  toolLabel: string | null
  httpMethod: string | null
  captureSeam: string | null
}

const FACT_KINDS: readonly CorrelationFactKind[] = ['receipt', 'audit', 'tool']
const METHOD = /^(GET|HEAD|POST|PUT|PATCH|DELETE|OPTIONS)$/

export function toCorrelationFact(row: Row): CorrelationFact | null {
  const kind = FACT_KINDS.find((k) => k === row.kind)
  if (!kind) return null
  const status = int(row.status_class)
  const method = text(row.http_method)
  return {
    kind,
    at: String(row.at ?? ''),
    action: text(row.action),
    operation: text(row.operation),
    entityLabel: governedLabel(row.entity_label),
    recordLabel: governedLabel(row.record_label),
    actorLabel: actorLabel(row.actor_label),
    onBehalfOfLabel: actorLabel(row.on_behalf_of_label),
    sourceKind: text(row.source_kind),
    sourceLabel: governedLabel(row.source_label) ?? sourceKindLabel(text(row.source_kind)),
    sourceTrust: sourceTrust(row.source_trust),
    clientLabel: governedLabel(row.client_label),
    outcome: text(row.outcome),
    statusClass: status != null && status >= 1 && status <= 5 ? status : null,
    durationMs: int(row.duration_ms),
    surface: text(row.surface),
    toolLabel: governedLabel(row.tool_label),
    httpMethod: method && METHOD.test(method) ? method : null,
    captureSeam: text(row.capture_seam),
  }
}

export async function correlationTrace(door: AuditDoor, correlationId: string): Promise<CorrelationFact[]> {
  const [fn, schema] = AUDIT_PROJECTIONS.correlationTrace
  const rows = await door.rpcTable<Row>(fn, { p_correlation_id: correlationId }, schema)
  return rows.map(toCorrelationFact).filter((fact): fact is CorrelationFact => fact !== null)
}

/* ── 5. tool_invocation_view ───────────────────────────────────────────────── */

export interface ToolInvocationRow {
  at: string
  toolLabel: string | null
  module: string | null
  outcome: string
  durationMs: number | null
  surface: string | null
  actorLabel: string | null
  onBehalfOfLabel: string | null
  sourceKind: string | null
  sourceLabel: string
  sourceTrust: SourceTrust | null
  clientLabel: string | null
  /** A join key for the correlation trace. Never displayed. */
  correlationId: string | null
  hasAudit: boolean
  hasRequestReceipt: boolean
}

/**
 * NEVER arguments, prompt, response or the stored reason: the row is rebuilt
 * from the contract's names and nothing else, whatever the server sent
 * (SPINE-P4 §4, p4-api §4).
 */
export function toToolInvocationRow(row: Row): ToolInvocationRow {
  return {
    at: String(row.at ?? ''),
    toolLabel: governedLabel(row.tool_label),
    module: text(row.module),
    outcome: text(row.outcome) ?? 'unknown',
    durationMs: int(row.duration_ms),
    surface: text(row.surface),
    actorLabel: actorLabel(row.actor_label),
    onBehalfOfLabel: actorLabel(row.on_behalf_of_label),
    sourceKind: text(row.source_kind),
    sourceLabel: governedLabel(row.source_label) ?? sourceKindLabel(text(row.source_kind)),
    sourceTrust: sourceTrust(row.source_trust),
    clientLabel: governedLabel(row.client_label),
    correlationId: correlation(row.correlation_id),
    hasAudit: bool(row.has_audit),
    hasRequestReceipt: bool(row.has_request_receipt),
  }
}

export async function toolInvocationView(
  door: AuditDoor, filter: ToolInvocationFilter, cursor: string | null = null, limit?: number,
): Promise<AuditPage<ToolInvocationRow>> {
  const [fn, schema] = AUDIT_PROJECTIONS.toolInvocations
  const rows = await door.rpcTable<Row>(fn, {
    p_filter: auditFilterArgs(filter), p_cursor: cursor, p_limit: pageLimit(limit),
  }, schema)
  return pageOf(rows, toToolInvocationRow)
}

/* ── 6. export ─────────────────────────────────────────────────────────────── */

/** The server refuses an export matching more than this many rows (22023). */
export const AUDIT_EXPORT_MAX_ROWS = 10_000

export type AuditExportKind = 'timeline' | 'tools'

/**
 * The export's rows: the SAME projection, the SAME authorization, through the
 * export doors (VOLATILE, so each export is itself audited as
 * `audit.exported`). The client only formats. The rows pass through the same
 * allowlist mappers as the screen.
 */
export async function auditExport(
  door: AuditDoor, kind: 'timeline', filter: AuditTimelineFilter,
): Promise<AuditTimelineRow[]>
export async function auditExport(
  door: AuditDoor, kind: 'tools', filter: ToolInvocationFilter,
): Promise<ToolInvocationRow[]>
export async function auditExport(
  door: AuditDoor, kind: AuditExportKind, filter: AuditTimelineFilter | ToolInvocationFilter,
): Promise<AuditTimelineRow[] | ToolInvocationRow[]> {
  const [fn, schema] = kind === 'timeline' ? AUDIT_PROJECTIONS.exportTimeline : AUDIT_PROJECTIONS.exportTools
  const rows = await door.rpcTable<Row>(fn, { p_filter: auditFilterArgs(filter) }, schema)
  return kind === 'timeline' ? rows.map(toAuditTimelineRow) : rows.map(toToolInvocationRow)
}

/**
 * One CSV from already-mapped rows. Every cell is quoted; a cell that would
 * start a spreadsheet formula (`= + - @`, tab, carriage return) is prefixed
 * with `'`, so an exported label can never execute in the reader's tool
 * (CSV injection). Lines end CRLF, per RFC 4180.
 */
export function auditCsv<R>(columns: readonly { header: string; value: (row: R) => unknown }[], rows: readonly R[]): string {
  const cell = (value: unknown): string => {
    let s = value == null ? '' : String(value)
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`
    return `"${s.replace(/"/g, '""')}"`
  }
  const lines = [columns.map((c) => cell(c.header)).join(',')]
  for (const row of rows) lines.push(columns.map((c) => cell(c.value(row))).join(','))
  return lines.join('\r\n') + '\r\n'
}

/* ── 7. audit_security_posture ─────────────────────────────────────────────── */

export const POSTURE_STATUSES = ['ok', 'warn', 'fail', 'info', 'not_installed'] as const
export type PostureStatus = typeof POSTURE_STATUSES[number]

export interface PostureFact {
  area: string
  /** The server's code — a governed identifier, never a sentence. */
  code: string
  status: PostureStatus
  /** A count, or `null` when the check has none (a setting). */
  count: number | null
  /** Only ever a server SETTING value (`ddl,role`, `warning`, `none`) or null. */
  detail: string | null
  at: string | null
}

const CODE = /^[a-z][a-z0-9_.:-]*$/

export function toPostureFact(row: Row): PostureFact | null {
  const code = text(row.code)
  if (!code || !CODE.test(code)) return null
  const status = POSTURE_STATUSES.find((s) => s === row.status) ?? 'info'
  const detail = text(row.detail)
  return {
    area: text(row.area) ?? 'other',
    code,
    status,
    count: int(row.count),
    /* A setting value is short and plain; anything else is not shown. */
    detail: detail && detail.length <= 120 && !UUID_ANYWHERE.test(detail) ? detail : null,
    at: text(row.at),
  }
}

export async function auditSecurityPosture(door: AuditDoor): Promise<PostureFact[]> {
  const [fn, schema] = AUDIT_PROJECTIONS.securityPosture
  const rows = await door.rpcTable<Row>(fn, {}, schema)
  return rows.map(toPostureFact).filter((fact): fact is PostureFact => fact !== null)
}

/**
 * The projections' names, in ONE table (p4-api, core 0.389.0). A rename on the
 * server is one line here; the console fixture dispatches on the same table so
 * the two cannot drift.
 */
export const AUDIT_PROJECTIONS = {
  isAuditReader: ['is_audit_reader', 'core'],
  recordLastChange: ['record_last_change', 'core'],
  recordHistory: ['record_history', 'core'],
  myToolInvocations: ['my_tool_invocations', 'core'],
  timeline: ['audit_timeline', 'core'],
  correlationTrace: ['correlation_trace', 'core'],
  toolInvocations: ['tool_invocation_view', 'core'],
  exportTimeline: ['export_audit_timeline', 'core'],
  exportTools: ['export_tool_invocations', 'core'],
  securityPosture: ['audit_security_posture', 'core'],
} as const satisfies Record<string, readonly [string, string]>
